import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Download a protected file (GET with auth) as a browser download.
export async function downloadProtected(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const blob = new Blob([res.data]);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export const ImportButton = ({ entity, label, onDone }) => {
  const { user } = useAuth();
  if (user?.role !== "admin") return null;
  return <ImportButtonInner entity={entity} label={label} onDone={onDone} />;
};

const ImportButtonInner = ({ entity, label, onDone }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const close = () => { setOpen(false); setFile(null); setResult(null); };

  const submit = async () => {
    if (!file) { toast.error("Selecione um ficheiro .csv ou .xlsx"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${entity}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.criados} criados · ${data.atualizados} atualizados`);
      onDone && onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="gap-1.5" data-testid={`btn-importar-${entity}`} onClick={() => setOpen(true)}>
        <Upload size={15} /> Importar
      </Button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent>
          <DialogHeader><DialogTitle>Importar {label} (Excel / CSV)</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Importar {label} a partir de um ficheiro Excel ou CSV</DialogDescription>
          <p className="text-xs text-slate-500">
            Carregue um ficheiro <b>.csv</b> ou <b>.xlsx</b> com as colunas do modelo. Registos existentes
            são atualizados ({entity === "artigos" ? "por referência" : "por NIF"}); os restantes são criados.
          </p>
          <button
            type="button"
            onClick={() => downloadProtected(`/import/${entity}/template`, `modelo_${entity}.csv`)}
            data-testid={`btn-modelo-${entity}`}
            className="flex items-center gap-1.5 text-xs text-sky-600 hover:text-sky-800 w-fit"
          >
            <Download size={13} /> Descarregar modelo de exemplo
          </button>

          <label
            className="mt-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-6 cursor-pointer hover:border-sky-400 transition-colors"
            data-testid={`drop-${entity}`}
          >
            <FileSpreadsheet size={22} className="text-slate-400" />
            <span className="text-sm text-slate-600">{file ? file.name : "Clique para escolher o ficheiro"}</span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              data-testid={`file-${entity}`}
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            />
          </label>

          {result && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1" data-testid="import-result">
              <div className="font-semibold text-slate-700">Resultado ({result.total} linha(s))</div>
              <div className="text-emerald-700">Criados: {result.criados} · Atualizados: {result.atualizados}</div>
              {result.erros?.length > 0 && (
                <div className="text-rose-600">
                  <div className="font-medium">Erros:</div>
                  {result.erros.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={close}>Fechar</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={submit} disabled={busy} data-testid={`btn-enviar-import-${entity}`}>
              {busy ? "A importar…" : "Importar ficheiro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
